import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cashTenderForOwed, formatMoney, moneyFromCents, sumMoney, type Money } from '@wodi/domain';
import type { BillView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  MoneyField,
  Notice,
  Select,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { billingKeys, takePayment } from '../api/billing.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ShortcutHint } from '../layout/screen.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './billing.module.css';
import { cents, METHOD_LABELS, money, type PaymentMethodKey } from './format.ts';

/** What each tender pays and the change on cash, worked out the way the server will. */
function previewTenders(tenders: readonly TenderDraft[], owed: Money) {
  let remaining = owed;
  const previews: { change: Money | null; pays: Money | null }[] = [];
  for (const t of tenders) {
    if (t.cents === null || t.cents <= 0n) {
      previews.push({ change: null, pays: null });
      continue;
    }
    if (t.method === 'cash') {
      const result = cashTenderForOwed({ owed: remaining, tendered: moneyFromCents(t.cents) });
      if (!result.ok) {
        previews.push({ change: null, pays: null });
        continue;
      }
      remaining = moneyFromCents(remaining.cents - result.value.amount.cents);
      previews.push({ change: result.value.change, pays: result.value.amount });
      continue;
    }
    remaining = moneyFromCents(remaining.cents > t.cents ? remaining.cents - t.cents : 0n);
    previews.push({ change: null, pays: moneyFromCents(t.cents) });
  }
  return { previews, remaining };
}

interface TenderDraft {
  method: PaymentMethodKey;
  /** Cash handed over, or the amount paid by card, bank or M-Pesa. */
  cents: bigint | null;
  reference: string;
}

const REFERENCE_LABEL: Record<Exclude<PaymentMethodKey, 'cash'>, string> = {
  mpesa: 'M-Pesa code',
  card: 'Card slip reference',
  bank_transfer: 'Bank reference',
};

/**
 * Take payment (cashier-and-payments.md §2): choose the lines, then one or more tenders.
 * Cash shows the change as it is typed. M-Pesa is recorded by its transaction code until
 * STK push exists (plan §13 decision 24). Ctrl/⌘Enter takes the payment.
 */
export function TakePaymentDialog({
  bill,
  isOpen,
  onOpenChange,
  onPaid,
}: {
  bill: BillView;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onPaid: (receiptIds: string[]) => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const payable = bill.charges.filter((c) => cents(c.owed) > 0n || c.state === 'owed');
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(payable.map((c) => c.id)));
  const [tenders, setTenders] = useState<TenderDraft[]>([
    { method: 'cash', cents: null, reference: '' },
  ]);
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [tried, setTried] = useState(false);

  const owedChosen = sumMoney(
    payable.filter((c) => chosen.has(c.id)).map((c) => moneyFromCents(cents(c.owed))),
  );

  const { previews, remaining } = previewTenders(tenders, owedChosen);

  const problems = tenders.map((t) => {
    if (t.cents === null || t.cents <= 0n) return 'Enter an amount.';
    if (t.method !== 'cash' && t.reference.trim().length < 3)
      return `Enter the ${REFERENCE_LABEL[t.method].toLowerCase()}.`;
    return null;
  });
  const valid = owedChosen.cents > 0n && problems.every((p) => p === null);

  const submit = useMutation({
    mutationFn: () =>
      takePayment(bill.visit.id, {
        clientRequestId: requestKey,
        chargeIds: payable.filter((c) => chosen.has(c.id)).map((c) => c.id),
        tenders: tenders.map((t) =>
          t.method === 'cash'
            ? { method: 'cash' as const, tenderedCents: (t.cents ?? 0n).toString() }
            : {
                method: t.method,
                amountCents: (t.cents ?? 0n).toString(),
                reference: t.reference.trim(),
              },
        ),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      const change = result.payments.find((p) => p.change !== null && p.change.cents !== '0');
      toasts.show({
        title: 'Payment received',
        description: change?.change ? `Change ${money(change.change)}` : 'Receipt printing',
      });
      setRequestKey(crypto.randomUUID());
      onPaid(result.payments.map((p) => p.receiptId));
      onOpenChange(false);
    },
  });

  const go = () => {
    setTried(true);
    if (valid && online && !submit.isPending) submit.mutate();
  };
  useShortcut('mod+enter', go, {
    allowInFields: true,
    enabled: isOpen,
    description: 'Take payment',
  });

  const update = (index: number, change: Partial<TenderDraft>) => {
    setTenders((list) => list.map((t, i) => (i === index ? { ...t, ...change } : t)));
  };

  return (
    <Dialog title="Take payment" isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className={styles.form}>
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Paying for</legend>
          {payable.map((c) => (
            <Checkbox
              key={c.id}
              isSelected={chosen.has(c.id)}
              onChange={(on) => {
                setChosen((set) => {
                  const next = new Set(set);
                  if (on) next.add(c.id);
                  else next.delete(c.id);
                  return next;
                });
              }}
            >
              {`${c.itemName}: ${money(c.owed)}`}
            </Checkbox>
          ))}
          <p className={styles.summary}>{`To pay ${formatMoney(owedChosen)}`}</p>
        </fieldset>

        {tenders.map((t, index) => {
          const preview = previews[index];
          return (
            <div key={index} className={styles.tenders}>
              <div className={styles.row}>
                <Select
                  label="Method"
                  options={(Object.keys(METHOD_LABELS) as PaymentMethodKey[]).map((m) => ({
                    id: m,
                    label: METHOD_LABELS[m],
                  }))}
                  value={t.method}
                  onChange={(v) => {
                    update(index, { method: v as PaymentMethodKey });
                  }}
                />
                <MoneyField
                  label={t.method === 'cash' ? 'Cash received' : 'Amount'}
                  value={t.cents}
                  onChange={(value) => {
                    update(index, { cents: value });
                  }}
                  {...(tried && problems[index] === 'Enter an amount.'
                    ? { errorMessage: 'Enter an amount.' }
                    : {})}
                  autoFocus={index === 0}
                />
                {t.method === 'cash' ? null : (
                  <TextField
                    label={REFERENCE_LABEL[t.method]}
                    value={t.reference}
                    onChange={(value) => {
                      update(index, { reference: value });
                    }}
                    {...(tried && problems[index] !== null && problems[index] !== 'Enter an amount.'
                      ? { errorMessage: problems[index] ?? '' }
                      : {})}
                  />
                )}
              </div>
              {preview?.change ? (
                <p className={styles.change}>{`Change ${formatMoney(preview.change)}`}</p>
              ) : null}
              {tenders.length > 1 ? (
                <div>
                  <Button
                    dense
                    variant="quiet"
                    onPress={() => {
                      setTenders((list) => list.filter((_, i) => i !== index));
                    }}
                  >
                    Remove this method
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
        {tenders.length < 4 ? (
          <div>
            <Button
              dense
              onPress={() => {
                setTenders((list) => [...list, { method: 'mpesa', cents: null, reference: '' }]);
              }}
            >
              Add another method
            </Button>
          </div>
        ) : null}
        {remaining.cents > 0n && previews.some((p) => p.pays !== null) ? (
          <p>{`${formatMoney(remaining)} will still be owed.`}</p>
        ) : null}
        {owedChosen.cents === 0n ? (
          <Notice tone="info">Choose at least one line to pay for.</Notice>
        ) : null}
        {!online ? (
          <Notice tone="info">
            Taking payment needs a connection to Wodi. Write a receipt from the receipt book and
            enter it here when Wodi is back.
          </Notice>
        ) : null}
        {submit.isError ? (
          <Notice tone="error">
            {errorText(submit.error, "The payment wasn't recorded. Try again.")}
          </Notice>
        ) : null}
        <ShortcutHint combo="mod+enter" action="take the payment" />
      </div>
      <DialogActions>
        <Button
          variant="quiet"
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={go}
          isPending={submit.isPending}
          pendingLabel="Taking payment"
          isDisabled={!online}
        >
          Take payment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
